import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Info, X } from "lucide-react";
import { componentStyles, cn } from "@/lib/design-system";

interface ProfileSubPageProps {
    title: string;
    subtitle: string;
    onBack: () => void;
    children: React.ReactNode;
    infoContent?: {
        title: string;
        content: React.ReactNode;
    };
    actionButtons?: React.ReactNode;
}

export const ProfileSubPage = ({
    title,
    subtitle,
    onBack,
    children,
    infoContent,
    actionButtons
}: ProfileSubPageProps) => {
    const [isInfoOpen, setIsInfoOpen] = useState(false);

    return (
        <div className="space-y-6" dir="rtl">
            {/* Header with Title, Back Button, and Info */}
            <div className="space-y-4">
                {/* Top row: Title and navigation buttons (RTL: title left, buttons right) */}
                <div className="flex items-start justify-between">
                    <div className="text-right">
                        <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
                        <p className="text-muted-foreground">{subtitle}</p>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Info Button */}
                        {infoContent && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsInfoOpen(true)}
                                className={componentStyles.navigation.infoButton}
                            >
                                <Info className="h-4 w-4" />
                                <span className="sr-only">מידע וטיפים</span>
                            </Button>
                        )}

                        {/* Back Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onBack}
                            className={componentStyles.navigation.backButton}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            <span className="sr-only">חזרה לתפריט הראשי</span>
                        </Button>
                    </div>
                </div>

                {/* Action buttons row - RTL: buttons flow right to left */}
                {actionButtons && (
                    <div className={componentStyles.navigation.actionButtonsContainer}>
                        {actionButtons}
                    </div>
                )}
            </div>

            {/* Main Content */}
            {children}

            {/* Info Dialog */}
            {infoContent && (
                <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
                    <DialogContent className="max-w-2xl" dir="rtl" hideCloseButton>
                        <DialogDescription className="sr-only">
                            עריכת פרטי הפרופיל
                        </DialogDescription>
                        {/* Custom Close Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className={componentStyles.dialog.closeButton}
                            onClick={() => setIsInfoOpen(false)}
                        >
                            <X className="h-4 w-4" />
                            <span className="sr-only">סגור</span>
                        </Button>

                        <DialogHeader className={componentStyles.dialog.header}>
                            <DialogTitle className={componentStyles.dialog.title}>
                                {infoContent.title}
                            </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4">
                            {infoContent.content}
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
};
